/**
 * Sesotho: Flows and chatbots
 *
 * What this file covers: The chatbot flow builder, its steps, AI answers, Build with AI, and the chatbots
 * themselves.
 *
 * Tone: Practical and a little technical, never heavy with jargon. Step names are short actions (Ask a
 * question, Book a time). Explanations say what the step does for the client.
 *
 * Sesotho: Everyday Sesotho as used in South African business. Keys not translated yet fall back to English, so
 * a missing key is never an error.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const flows: Catalog = {
  'flows.title': 'Liphallo',
  'flows.builder.title': 'Mohahi',
  'flows.builder.saveFailed': 'Ho boloka ho hlolehile',
  'flows.builder.unsaved': 'Liphetoho tse sa bolokoang',
  'flows.builder.canvasTab': 'Khanvase',
  'flows.builder.palette': 'Linoto',
  'flows.builder.properties': 'Litšobotsi',
  'flows.builder.group.start': 'Qala',
  'flows.builder.group.steps': 'Mehato',
  'flows.builder.node.trigger': 'Sesusumetsi',
  'flows.builder.node.reply': 'Romela karabo',
  'flows.builder.node.handoff': 'Fetisetsa',
  'flows.builder.field.keyword': 'Lentsoe la bohlokoa',
  'flows.builder.field.body': 'Molaetsa',
  'flows.drawer.definition': 'Tlhaloso',
  'flows.drawer.nameRequired': 'Lebitso lea hlokahala.',
  'flows.empty': 'Ha ho liphallo hajoale: theha e le \'ngoe ho iketsetsa likarabo.',
  'flows.loadError': 'Ha ea khona ho kenya liphallo.',
  'flows.modal.delete.confirm': 'Hlakola phallo',
  'flows.modal.delete.title': 'Hlakola phallo?',
  'flows.runs.empty': 'Ha ho lipoto hajoale bakeng sa phallo ena.',
  'flows.view.backToAll': 'Liphallo tsohle',
  'flows.view.lifecycle.activateFlow': 'Sebelisa phallo',
  'flows.view.lifecycle.activation': 'Ho sebetsa',
  'flows.view.lifecycle.deactivateFlow': 'Emisa phallo',
  'flows.view.lifecycle.deleteFailed': 'Ha ea khona ho hlakola phallo.',
  'flows.view.lifecycle.updateFailed': 'Ha ea khona ho nchafatsa phallo.',
  'flows.view.overview.flowId': 'ID ea phallo',
  'flows.view.overview.title': 'Lintlha tsa phallo',
  'flows.view.runs.columns.contact': 'Motho',
  'flows.view.runs.columns.duration': 'Bolelele ba nako',
  'flows.view.runs.columns.error': 'Phoso',
  'flows.view.runs.title': 'Lipoto tsa morao tjena',
  'flows.view.tabs.runs': 'Lipoto',
  'flows.actions.create': 'Theha phallo',
  'flows.showingOf': 'Ho bontsšoa {{count}} ho {{total}}',
  'flows.node.trigger': 'Sesusi',
  'flows.node.reply': 'Romela karabo',
  'flows.builder.save': 'Boloka liphetoho',
  'flows.connect.metaChannelLive': 'e araba le mona, hang ha leqephe le hokahantswe.',
  'flows.connect.metaChannelWhere': 'Hokahanya leqephe la Facebook kapa akhaonto ya Instagram ho',
  'bots.switcher.label': 'Chatbot',
  'bots.switcher.hint': 'O sheba mosebetsi wa chatbot ena.',
  'bots.switcher.flowsHint': 'Diphallo tse ka tlase ke tsa chatbot ena. Diphallo tse ntjha di ekeletswa ho yona.',
  'bots.switcher.knowledgeHint': 'Chatbot ka nngwe e na le dikarabo tsa yona tse iketsahallang. Ditokomane di arolelanwa ke tsohle.',
  'bots.switcher.defaultOption': '{{name}} (ya kamehla)',
  'bots.channels.title': 'Moo chatbot ena e arabang teng',
  'bots.channels.intro': 'Tshwaya dinomoro, dipuisano tsa websaete le diakhaonto tseo chatbot ena e lokelang ho araba ho tsona. E nngwe le e nngwe e arajwa ke chatbot e le nngwe, kahoo ho e tshwaya mona ho e nka ho chatbot e neng e na le yona.',
  'bots.channels.introDefault': 'Ena ke chatbot ya hao ya kamehla: e araba hohle moo ho sa kgethwang chatbot e nngwe. Ho fa chatbot e nngwe nomoro kapa puisano, bula chatbot eo ebe o e tshwaya moo.',
  'bots.channels.noFlows': '{{name}} ha e so be le diphallo, kahoo bareki ba ngolang mona ha ba fumane karabo. Aha e le nngwe ho:',
  'bots.channels.none': 'Ha o so hokele dinomoro, dipuisano tsa websaete kapa diakhaonto. Qala ho:',
  'bots.channels.kind.whatsapp': 'WhatsApp',
  'bots.channels.kind.web': 'Puisano ya websaete',
  'bots.channels.kind.messenger': 'Messenger',
  'bots.channels.kind.instagram': 'Instagram',
  'bots.channels.mineByDefault': 'E araba mona hobane ha ho chatbot e nngwe e kgethilweng.',
  'bots.channels.answeredBy': 'Hona jwale e arajwa ke {{name}}.',
  'bots.channels.moved': '{{account}} jwale e arajwa ke {{name}}.',
  'bots.channels.returned': '{{account}} e kgutletse ho {{name}}.',
  'bots.channels.confirm.title': 'E fetisetse ho chatbot ena?',
  'bots.channels.confirm.body': '{{account}} e arajwa ke {{from}} kajeno. Ho tloha molaetseng o latelang e tla arajwa ke {{to}}. Puisano e seng e ntse e tswela pele e tla phethwa ke {{from}}.',
  'bots.channels.confirm.action': 'E fetise',
  'bots.actions.title': 'Ya kamehla le ho hlakola',
  'bots.actions.isDefault': 'Ena ke chatbot ya hao ya kamehla. E araba dinomorong tsohle, dipuisanong tsa websaete le diakhaontong tse sa fuwang chatbot e nngwe, kahoo e ke ke ya hlakolwa. Ho e hlakola, etsa chatbot e nngwe ya kamehla pele.',
  'bots.actions.defaultHint': 'Chatbot ya kamehla e araba dinomorong tsohle, dipuisanong tsa websaete le diakhaontong tse sa fuwang chatbot e nngwe.',
  'bots.actions.madeDefault': '{{name}} jwale ke chatbot ya hao ya kamehla.',
  'bots.actions.deleted': '{{name}} e hlakotswe.',
  'bots.actions.makeDefault.title': 'Etsa {{name}} ya kamehla?',
  'bots.actions.makeDefault.body': '{{name}} e tla araba hohle moo ho sa kgethwang chatbot, ho tloha molaetseng o latelang. Diphallo di dula le chatbot eo e leng tsa yona.',
  'bots.actions.makeDefault.action': 'Etsa ya kamehla',
  'bots.actions.delete.title': 'Hlakola {{name}}?',
  'bots.actions.delete.body': 'Chatbot ena ha e na diphallo mme ha e arabe kae kapa kae, kahoo ha ho letho le leng le fetohang. Dipuisano tsa nako e fetileng di boloka nalane ya tsona.',
  'bots.actions.delete.action': 'Hlakola chatbot',
  'bots.actions.delete.hint': 'Ho hlakola ho tlosa lebitso le disetting tsa chatbot ena. Dipuisano tsa nako e fetileng di boloka nalane ya tsona.',
  'bots.actions.delete.hintInUse': 'Chatbot ena e na le diphallo kapa e araba akhaontong. O tla botswa hore ke chatbot efe e di nkang.',
  'bots.actions.move.body': '{{name}} e ntse e na le mosebetsi: diphallo {{flows}}, diakhaonto {{accounts}}. Kgetha chatbot e di nkang. Di fetiswa mohatong o le mong le ho hlakola.',
  'bots.actions.move.label': 'Fetisetsa tsohle ho',
  'bots.actions.move.hint': 'Chatbot eo e tla tsamaisa diphallo tsena mme e arabe diakhaontong tsena ho tloha molaetseng o latelang.',
  'bots.actions.move.action': 'Fetisa ebe o hlakola',
  'bots.add.title': 'Eketsa chatbot',
  'bots.add.intro': 'Chatbot e na le lebitso la yona, tsela ya yona ya ho bua le diphallo tsa yona. Kamora ho e eketsa, kgetha moo e arabang teng ebe o aha diphallo tsa yona.',
  'bots.add.copyFrom': 'Qala ho tswa ho',
  'bots.add.copyFromHint': 'E kopitsa molumo, puo, dikarabo tsa puisano e kgutshwane, manane a mantswe le ditsebiso. Diphallo le diakhaonto ha di kopitswe.',
  'bots.add.copyNothing': 'Chatbot e se nang letho',
  'bots.add.action': 'Eketsa chatbot',
  'bots.add.done': '{{name}} e ekeditswe. Kgetha moo e arabang teng, ebe o aha diphallo tsa yona.',
  'bots.add.cardTitle': 'Chatbot e nngwe',
  'bots.add.cardBody': 'Tsamaisa letshwao la bobedi, puo kapa lefapha ka lebitso la lona, diphallo le dinomoro tsa lona. Chatbot e le nngwe e ka araba dinomorong tse ngata, kapa nomoro ka nngwe e be le ya yona.',
  'bots.add.atLimit': 'Di-chatbot tse kenyeleditsweng ho {{plan}}: {{count}}. Nyolohela morerong o moholo ho eketsa e nngwe.',
  'bots.add.seePlans': 'Sheba merero',
  'bots.list.title': 'Di-chatbot tsa hao',
  'bots.list.intro': 'Chatbot ka nngwe e na le lebitso la yona, diphallo tsa yona le dinomoro le dipuisano tseo e arabang ho tsona. Bula e nngwe ho e fetola.',
  'bots.list.default': 'Ya kamehla',
  'bots.list.answers': 'Dinomoro le dipuisano tse hoketsweng: {{count}}',
  'bots.list.answersDefault': 'E araba hohle moo ho sa kgethwang chatbot e nngwe',
  'bots.list.flows': 'Diphallo: {{count}}',
  'bots.loadError': 'Di-chatbot tsa hao ha di a kgona ho jarwa. Kgatholla leqephe ho leka hape.',
  'bots.backToList': 'Kgutlela ho di-chatbot tsa hao',
  'bots.notFound': 'Chatbot eo ha e sa le teng.',
  'bots.flow.label': 'Chatbot',
  'bots.flow.hint': 'Ke chatbot ena feela e tsamaisang phallo ena. Ho e fetisa ho qala molaetseng o latelang.',
  'bots.header.count': '{{current}}/{{allowance}} · {{plan}}',
  'bots.header.countUnlimited': '{{current}} · {{plan}}, ha ho moedi',
  'bots.header.single': '{{name}} o araba dinomorong tsohle tsa hao le dipuisanong. Eketsa chatbot ho tsamaisa letshwao la bobedi, puo kapa lefapha ka lebitso la lona, diphallo le dinomoro tsa lona.'
};

export default flows;
